import { useState, useEffect, useCallback } from 'react';
import OutlookSync from './OutlookSync';
import { useIsHQ, useRole } from '../../hooks/useIsHQ';
import { crmService } from '../../services/crmService';

// ─── Shared style tokens ───────────────────────────────────────────────────────

const surface = { background: '#111827', border: '1px solid #1E2D45', borderRadius: 8, padding: '20px 24px' };

function SectionTitle({ children }) {
  return <h2 style={{ margin: '0 0 20px', color: '#F1F5F9', fontSize: 16, fontWeight: 700 }}>{children}</h2>;
}

// ─── Integrations section ──────────────────────────────────────────────────────

function IntegrationsSection() {
  return (
    <div>
      <SectionTitle>Integrations</SectionTitle>
      <OutlookSync />
    </div>
  );
}

// ─── Dark Horse export section (HQ only) ────────────────────────────────────────

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function DarkHorseSection() {
  const [configured, setConfigured] = useState(null); // null = unknown
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);   // { objectKey, counts }
  const [error, setError] = useState(null);

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  });

  useEffect(() => {
    fetch(`${API_BASE}/api/crm/dark-horse/status`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : { configured: false }))
      .then((d) => setConfigured(Boolean(d.configured)))
      .catch(() => setConfigured(false));
  }, []);

  const runExport = async () => {
    setRunning(true); setError(null); setResult(null);
    try {
      const res = await fetch(`${API_BASE}/api/crm/dark-horse/export`, {
        method: 'POST', headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || `Export failed (${res.status})`);
      setResult({ objectKey: data.objectKey, counts: data.counts });
    } catch (e) {
      setError(e.message);
    } finally {
      setRunning(false);
    }
  };

  const countLine = result
    ? Object.entries(result.counts).map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`).join('  ·  ')
    : null;

  return (
    <div>
      <SectionTitle>Dark Horse</SectionTitle>
      <div style={{ ...surface, maxWidth: 640 }}>
        <p style={{ margin: '0 0 16px', color: '#94A3B8', fontSize: 13, lineHeight: 1.6 }}>
          Push a signed, encrypted snapshot of the CRM (org graph, offices, materials,
          products, competitors, contacts, and engagements) to Dark Horse. After it
          finishes, click <strong style={{ color: '#F1F5F9' }}>Update</strong> in Dark Horse
          to ingest and recompute.
        </p>

        {configured === false && (
          <div style={{
            background: '#7F1D1D22', border: '1px solid #7F1D1D', borderRadius: 6,
            padding: '10px 14px', color: '#FCA5A5', fontSize: 12, marginBottom: 16,
          }}>
            Export is not configured on the server. Set the DH_* environment variables
            (signing key, data key, and R2 write token) to enable it.
          </div>
        )}

        <button
          onClick={runExport}
          disabled={running || configured === false}
          style={{
            background: running || configured === false ? '#1E2D45' : '#0E7490',
            color: running || configured === false ? '#64748B' : '#ECFEFF',
            border: '1px solid #22D3EE44', borderRadius: 6,
            padding: '9px 18px', fontSize: 13, fontWeight: 600,
            cursor: running || configured === false ? 'default' : 'pointer',
          }}
        >
          {running ? 'Exporting…' : 'Export to Dark Horse'}
        </button>

        {result && (
          <div style={{ marginTop: 16 }}>
            <div style={{ color: '#44AA66', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              Export complete
            </div>
            <div style={{ color: '#64748B', fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' }}>
              {result.objectKey}
            </div>
            <div style={{ color: '#94A3B8', fontSize: 12, marginTop: 8 }}>{countLine}</div>
          </div>
        )}

        {error && (
          <div style={{ marginTop: 16, color: '#FCA5A5', fontSize: 12 }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Account section ───────────────────────────────────────────────────────────

function AccountSection() {
  const role = useRole();

  function decodePayload() {
    try {
      const token = localStorage.getItem('token');
      if (!token) return {};
      return JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    } catch { return {}; }
  }

  const payload = decodePayload();

  const roleColor = role === 'headquarters' ? '#22D3EE' : '#94A3B8';
  const roleLabel = role === 'headquarters' ? 'Headquarters' : 'Standard';

  return (
    <div>
      <SectionTitle>Account</SectionTitle>
      <div style={{ ...surface, maxWidth: 500 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Row label="Email">
            <span style={{ color: '#F1F5F9', fontSize: 13 }}>{payload.email || '—'}</span>
          </Row>
          <Row label="Role">
            <span style={{
              background: roleColor + '22', color: roleColor,
              border: `1px solid ${roleColor}44`, borderRadius: 4,
              padding: '3px 10px', fontSize: 11, fontWeight: 700,
            }}>
              {roleLabel.toUpperCase()}
            </span>
          </Row>
          <Row label="User ID">
            <span style={{ color: '#64748B', fontFamily: 'monospace', fontSize: 12 }}>{payload.id || '—'}</span>
          </Row>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <div style={{ width: 100, color: '#64748B', fontSize: 12, fontWeight: 600, letterSpacing: '0.04em', flexShrink: 0 }}>
        {label.toUpperCase()}
      </div>
      {children}
    </div>
  );
}

// ─── Users section (HQ only) ───────────────────────────────────────────────────

function UsersSection() {
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(null); // id of user being toggled

  const load = useCallback(async () => {
    setLoading(true);
    try { setUsers(await crmService.getUsers()); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRoleChange = async (user, newRole) => {
    setSaving(user.id);
    try {
      await crmService.setUserRole(user.id, newRole);
      await load();
    } catch (e) { alert(e.message); }
    finally { setSaving(null); }
  };

  return (
    <div>
      <SectionTitle>Users</SectionTitle>
      {loading ? (
        <div style={{ color: '#64748B', padding: 20 }}>Loading…</div>
      ) : (
        <div style={{ ...surface, padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #1E2D45' }}>
                {['Email', 'Role', 'Joined', 'Change Role'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: '#64748B', fontSize: 11, fontWeight: 600, letterSpacing: '0.05em' }}>
                    {h.toUpperCase()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} style={{ borderBottom: '1px solid #0D1117' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#0D1F3C22'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '12px 16px', color: '#E2E8F0' }}>{u.email}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{
                      background: u.role === 'headquarters' ? '#22D3EE22' : '#1E2D45',
                      color: u.role === 'headquarters' ? '#22D3EE' : '#94A3B8',
                      border: `1px solid ${u.role === 'headquarters' ? '#22D3EE44' : '#374151'}`,
                      borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600,
                    }}>
                      {u.role === 'headquarters' ? 'Headquarters' : 'Standard'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', color: '#64748B', fontSize: 11 }}>
                    {new Date(u.created_at).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {saving === u.id ? (
                      <span style={{ color: '#64748B', fontSize: 12 }}>Saving…</span>
                    ) : u.role === 'headquarters' ? (
                      <button
                        onClick={() => handleRoleChange(u, 'standard')}
                        style={{ background: 'transparent', color: '#F59E0B', border: '1px solid #F59E0B44', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', fontSize: 11 }}
                      >
                        Revoke HQ
                      </button>
                    ) : (
                      <button
                        onClick={() => handleRoleChange(u, 'headquarters')}
                        style={{ background: 'transparent', color: '#22D3EE', border: '1px solid #22D3EE44', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', fontSize: 11 }}
                      >
                        Grant HQ
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Main ──────────────────────────────────────────────────────────────────────

export default function CRMSettings({ initialSection = 'integrations' }) {
  const isHQ = useIsHQ();
  const [section, setSection] = useState(initialSection);

  const NAV = [
    { key: 'integrations', label: 'Integrations' },
    { key: 'account',      label: 'Account' },
    ...(isHQ ? [{ key: 'users', label: 'Users' }, { key: 'darkhorse', label: 'Dark Horse' }] : []),
  ];

  return (
    <div style={{ display: 'flex', minHeight: '100%', padding: '24px' }}>
      {/* Left sidebar */}
      <div style={{ width: 180, flexShrink: 0, marginRight: 32 }}>
        <div style={{ fontSize: 10, color: '#4B5563', fontWeight: 700, letterSpacing: '0.1em', marginBottom: 12 }}>
          SETTINGS
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {NAV.map(n => (
            <button
              key={n.key}
              onClick={() => setSection(n.key)}
              style={{
                background: section === n.key ? '#1E2D45' : 'transparent',
                color: section === n.key ? '#F1F5F9' : '#6B7280',
                border: 'none', borderRadius: 6,
                padding: '8px 12px', cursor: 'pointer',
                fontSize: 13, fontWeight: section === n.key ? 600 : 400,
                textAlign: 'left', width: '100%',
              }}
            >
              {n.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {section === 'integrations' && <IntegrationsSection />}
        {section === 'account'      && <AccountSection />}
        {section === 'users' && isHQ && <UsersSection />}
        {section === 'darkhorse' && isHQ && <DarkHorseSection />}
      </div>
    </div>
  );
}
